"use client";

import { type FC, type ReactNode, useEffect, useRef } from "react";
import { Form, FormSectionContext } from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";

import { useMaximumMortgageWizard } from "../../../hooks/useMaximumMortgageWizard";
import { slideContainerStyle, slideContentStyle } from "./BaseSlide.css";
import type { MaximumMortgageWizardSlide } from "../../../types/MaximumMortgageWizard";
import { SlideHeading } from "./SlideHeading";

// biome-ignore lint/complexity/noBannedTypes: empty base props type extended by slide components
export type BaseSlideProps = {};

type BaseSlideComponentProps = {
  slideKey: MaximumMortgageWizardSlide;
  children: ReactNode;
  autoValidate?: boolean;
  className?: string;
  contentClassName?: string;
  title?: string;
  description?: string;
};

/**
 * Base slide component that provides common functionality for all wizard slides
 * Handles form registration, validation, and navigation logic
 */
export const BaseSlide: FC<BaseSlideComponentProps> = ({
  slideKey,
  children,
  autoValidate = false,
  className,
  contentClassName,
  title,
  description,
}) => {
  const {
    setSlideValidation,
    registerForm,
    navigateToNext,
    loading,
    onFieldSchemaChange,
  } = useMaximumMortgageWizard();
  const formRef = useRef<HTMLFormElement>(null);

  // Register this form with the wizard context
  useEffect(() => {
    registerForm(slideKey, formRef);
  }, [registerForm, slideKey]);

  // Auto-validate if specified (useful for optional slides)
  useEffect(() => {
    if (autoValidate) {
      setSlideValidation(slideKey, true);
    }
  }, [setSlideValidation, slideKey, autoValidate]);

  /**
   * Handle form submission and navigation
   */
  const handleSubmit = (e: React.FormEvent, valid: boolean) => {
    e.preventDefault();
    setSlideValidation(slideKey, valid);

    // If valid, navigate to next slide or submit wizard
    if (valid) {
      navigateToNext();
    }
  };

  return (
    <FormSectionContext.Provider value={slideKey}>
      <div className={mergeClasses(slideContainerStyle, className)}>
        {title && <SlideHeading title={title} description={description} />}
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          onSchemaChange={(schema) => {
            onFieldSchemaChange?.(slideKey, schema);
          }}
          className={mergeClasses(slideContentStyle, contentClassName)}
          disabled={loading}
        >
          {children}
        </Form>
      </div>
    </FormSectionContext.Provider>
  );
};
